import { useConnectionState, useRoomContext } from '@livekit/components-react';
import { ConnectionQuality, ConnectionState, RoomEvent } from 'livekit-client';
import { useEffect, useMemo, useState } from 'react';

const QUALITY_ORDER = [
	ConnectionQuality.Lost,
	ConnectionQuality.Poor,
	ConnectionQuality.Good,
	ConnectionQuality.Excellent
];

const worstOf = (a, b) => {
	const indexA = QUALITY_ORDER.indexOf(a);
	const indexB = QUALITY_ORDER.indexOf(b);

	if (indexA === -1) {
		return b;
	}

	if (indexB === -1) {
		return a;
	}

	return indexA <= indexB ? a : b;
};

const LABELS = {
	good: 'Conexão boa',
	poor: 'Conexão ruim',
	lost: 'Sem conexão',
	reconnecting: 'Reconectando',
	connected: 'Conectado',
	connecting: 'Conectando'
};

const LEVEL_BY_QUALITY = {
	[ConnectionQuality.Excellent]: 'good',
	[ConnectionQuality.Good]: 'good',
	[ConnectionQuality.Poor]: 'poor',
	[ConnectionQuality.Lost]: 'lost'
};

const useCallQuality = () => {
	const room = useRoomContext();
	const connectionState = useConnectionState();

	const [{ hasRemote, localQuality, remoteQuality }, setQualities] = useState({
		hasRemote: false,
		localQuality: ConnectionQuality.Unknown,
		remoteQuality: ConnectionQuality.Unknown
	});

	useEffect(() => {
		const sync = () => {
			const remote = Array.from(room.remoteParticipants.values())[0];

			setQualities({
				hasRemote: !!remote,
				localQuality: room.localParticipant.connectionQuality,
				remoteQuality: remote?.connectionQuality ?? ConnectionQuality.Unknown
			});
		};

		sync();

		room.on(RoomEvent.ConnectionQualityChanged, sync);
		room.on(RoomEvent.ParticipantConnected, sync);
		room.on(RoomEvent.ParticipantDisconnected, sync);

		return () => {
			room.off(RoomEvent.ConnectionQualityChanged, sync);
			room.off(RoomEvent.ParticipantConnected, sync);
			room.off(RoomEvent.ParticipantDisconnected, sync);
		};
	}, [room]);

	return useMemo(() => {
		if (connectionState === ConnectionState.Reconnecting
			|| connectionState === ConnectionState.SignalReconnecting) {
			return { level: 'reconnecting', label: LABELS.reconnecting };
		}

		if (connectionState !== ConnectionState.Connected) {
			return { level: 'connecting', label: LABELS.connecting };
		}

		const quality = hasRemote ? worstOf(localQuality, remoteQuality) : localQuality;

		const level = LEVEL_BY_QUALITY[quality] ?? 'connected';

		return { level, label: LABELS[level] };
	}, [connectionState, hasRemote, localQuality, remoteQuality]);
};

export default useCallQuality;
